"use client";

import React from "react";
import { ArrowRight, Phone, TrendingUp, ShieldCheck, Activity } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function Hero() {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Hotel Revenue Management in Mount Abu",
    "serviceType": "Hospitality Revenue Management & OTA Consulting",
    "provider": {
      "@type": "ProfessionalService",
      "name": "HotelRevBaBa",
      "url": "https://hotelrevbaba.com",
      "telephone": "+91-79721-95929"
    },
    "areaServed": {
      "@type": "City",
      "name": "Mount Abu",
      "containedInPlace": {
        "@type": "Country",
        "name": "India"
      }
    },
    "description": "Specialized hotel revenue management, dynamic pricing strategies, and OTA channel optimization for hotels, hill-station retreats, and lakeside resorts across Mount Abu.",
    "offers": {
      "@type": "Offer",
      "url": "https://hotelrevbaba.com/HotelRevenueManagementInMountAbu",
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock"
    }
  };
  const proofStats = [{
    metric: "+12% to +28%",
    label: "Average RevPAR growth",
    description: "Measured across independent and boutique properties in Mount Abu within 90 days.",
    icon: TrendingUp
  }, {
    metric: "<2%",
    label: "Parity violations",
    description: "Strict automated OTA monitoring to protect your direct booking advantage.",
    icon: ShieldCheck
  }, {
    metric: "24/7",
    label: "Rate intelligence",
    description: "Algorithmic market tracking calibrated for local seasonal demand peaks.",
    icon: Activity
  }];
  return <section id="hero" data-nav="dark" className="relative overflow-hidden bg-white text-foreground py-16 md:py-24 lg:py-32 isolate">
      {/* JSON-LD Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(schemaData)
    }} />

      {/* Decorative ambient gradients */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-lg bg-secondary/10 blur-3xl" />
        <div className="absolute top-1/2 -right-32 h-[30rem] w-[30rem] rounded-lg bg-secondary/15 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          {/* Eyebrow badge */}
          <div className="inline-flex items-center gap-2 mb-6">
            <Badge variant="secondary" className="bg-secondary/20 text-secondary border border-secondary/30 px-3.5 py-1 text-xs sm:text-sm font-semibold tracking-wide uppercase">
              Mount Abu Hospitality Consulting
            </Badge>
          </div>

          {/* Main H1 */}
          <h1 className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.15]">
            Hotel revenue management & OTA distribution consulting in Mount Abu
          </h1>

          {/* Subheadline */}
          <p className="mt-6 text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
            Stop losing up to 20% of your margins to OTA commissions and unmonitored pricing leaks. We help independent, boutique, and chain properties maximize RevPAR, protect rate parity, and accelerate direct bookings.
          </p>

          {/* Call to action cluster */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <Button asChild size="lg" className="w-full sm:w-auto bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold px-8 py-6 text-base shadow-lg transition-transform hover:-translate-y-0.5 active:translate-y-0">
              <Link to="#" className="inline-flex items-center justify-center gap-2">
                <span>Schedule your free revenue audit</span>
                <ArrowRight className="size-5" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-primary/30 bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-6 text-base shadow-sm">
              <a href="tel:+917972195929" className="inline-flex items-center justify-center gap-2">
                <Phone className="size-5 text-secondary" />
                <span>+91 79721 95929</span>
              </a>
            </Button>
          </div>

          <p className="mt-4 text-xs sm:text-sm text-muted-foreground">
            Confidential evaluation · No long-term retainer required
          </p>
        </div>

        {/* Proof Bar / Key Performance Indicators & Visual */}
        <div className="mt-16 md:mt-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
            {/* Visual element representing Mount Abu resort scene */}
            <div className="lg:col-span-4 relative rounded-xl overflow-hidden border border-border bg-card shadow-xl group min-h-[220px]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/mount_abu_luxury_resort_revenue.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/mount_abu_luxury_resort_revenue.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/mount_abu_luxury_resort_revenue.png-wvc-sizes" alt="Luxury resort and heritage property overlooking Mount Abu hills optimized with hotel revenue management" className="w-full h-full object-cover min-h-[220px] transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/30 to-transparent flex flex-col justify-end p-5">
                <Badge variant="secondary" className="bg-secondary/20 text-secondary border border-secondary/30 text-xs w-fit mb-1.5">
                  Mount Abu & Hill Station Strategy
                </Badge>
                <p className="text-xs text-primary-foreground/90 font-medium">
                  Hill resort & boutique hotel yield optimization
                </p>
              </div>
            </div>

            {/* Performance metric cards */}
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {proofStats.map((stat, i) => {
              const IconComponent = stat.icon;
              return <div key={stat.label} data-index={i}>
                    <Card className="h-full border border-border bg-card text-foreground shadow-xl transition-[border-color,background-color] duration-300 hover:border-secondary/50">
                      <CardContent className="p-6 md:p-6 flex flex-col justify-between h-full">
                        <div>
                          <div className="flex items-center justify-between gap-4 mb-4">
                            <span className="font-default text-2xl lg:text-3xl font-extrabold tracking-tight text-secondary">
                              {stat.metric}
                            </span>
                            <div className="p-2 rounded-lg bg-secondary/15 text-secondary border border-secondary/20 shrink-0">
                              <IconComponent className="size-5" />
                            </div>
                          </div>
                          <h2 className="font-default text-base font-semibold text-foreground">
                            {stat.label}
                          </h2>
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground leading-normal">
                          {stat.description}
                        </p>
                      </CardContent>
                    </Card>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}